import React, {useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Container,
    Divider,
    FormControlLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    InputBase,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {ICreateCartBundleRequest} from '../../../interfaces/fetch/cartBundle';
import instance from '../../../instance';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {CircularProgress} from '@material-ui/core';
import {ConfirmPayment} from '../ConfirmPayment/ConfirmPayment';
import {pluralize} from '../../../functionts/pluralize';
import CallNotification from '../../../context/NotificationContext';
import ServiceRentIcon from '../../../ui/ServiceRentIcon';
import StatusIcon from '../../../ui/StatusIcon';
import HelpPopover from '../../../ui/HelpPopover';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';

interface ICustomPaymentPage {
    userId: number | null;
}

const MONTHS_MAX = 12;
const clampMonths = (n: number) => Math.min(MONTHS_MAX, Math.max(1, n));

interface IMonthsStepper {
    value: number;
    inputValue: string;
    onChange: (value: number, inputValue: string) => void;
    compact?: boolean;
}

// Поле можно стереть до пустого – тогда считаем 1, при уходе с поля значение нормализуется
const MonthsStepper = ({value, inputValue, onChange, compact}: IMonthsStepper) => {
    const size = compact ? 24 : 28;
    const set = (n: number) => onChange(clampMonths(n), String(clampMonths(n)));
    return (
        <Box
            display="flex"
            alignItems="center"
            border="1px solid #ccc"
            borderRadius="6px"
            width={compact ? '92px' : '100px'}
            height={compact ? '32px' : '36px'}
            justifyContent="space-between"
            px={compact ? 0.5 : 1}
        >
            <IconButton size="small" onClick={() => set(value - 1)} sx={{width: size, height: size}} disabled={value <= 1}>
                <RemoveIcon fontSize="small" />
            </IconButton>
            <InputBase
                value={inputValue}
                onChange={e => {
                    const raw = e.target.value.replace(/\D/g, '');
                    if (raw === '') {
                        onChange(1, '');
                    } else {
                        const n = clampMonths(Number(raw));
                        onChange(n, String(n));
                    }
                }}
                onBlur={() => onChange(value, String(value))}
                inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                sx={{width: size, '& .MuiInputBase-input': {textAlign: 'center', padding: 0, lineHeight: 1.2}}}
            />
            <IconButton size="small" onClick={() => set(value + 1)} sx={{width: size, height: size}} disabled={value >= MONTHS_MAX}>
                <AddIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

const CustomPaymentPage = (props: ICustomPaymentPage) => {
    const {userId} = props;
    const navigate = useNavigate();
    const {handleErrorNotification} = CallNotification();
    const [cartData, setCartData] = useState<any[]>([]);
    const [totals, setTotals] = useState({totalSum: 0, totalCount: 0});
    const [hidePaid, setHidePaid] = useState(false);

    const [searchCompany, setSearchCompany] = useState('');
    const [searchKkm, setSearchKkm] = useState('');

    const [cartBundle, setCartBundle] = React.useState<any>({});
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [errorText, setErrorText] = React.useState<string>('');

    const [showConfirmPayment, setShowConfirmPayment] = useState(false);
    const [confirmPaymentData, setConfirmPaymentData] = useState<{
        merchantListWithIncludedCash: any[];
        totalCount: number;
        totalSum: number;
    }>({
        merchantListWithIncludedCash: [],
        totalCount: 0,
        totalSum: 0,
    });
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));

    // Общий счётчик задаёт месяцы всем кассам разом; у отдельной кассы месяцы можно поменять в её строке
    const [quantityAll, setQuantityAll] = useState<number>(1);
    const [quantityAllInput, setQuantityAllInput] = useState<string>('1');

    const handleKkmQuantity = (entityId: number, kkmId: number, value: number, inputValue: string) => {
        const newData = cartData.map(entity => {
            if (entity.id !== entityId) return entity;
            return {
                ...entity,
                kkm_list: entity.kkm_list.map((kkm: any) =>
                    kkm.kkm_id === kkmId ? {...kkm, quantity: value, quantity_input: inputValue} : kkm,
                ),
            };
        });
        setCartData(newData);
        recalculateTotals(newData);
    };

    const handleQuantityAll = (value: number, inputValue: string) => {
        setQuantityAll(value);
        setQuantityAllInput(inputValue);
        const newData = cartData.map(entity => ({
            ...entity,
            kkm_list: entity.kkm_list.map((kkm: any) => ({...kkm, quantity: value, quantity_input: String(value)})),
        }));
        setCartData(newData);
        recalculateTotals(newData);
    };

    const customMonthsCount = cartData.reduce(
        (count, entity) => count + entity.kkm_list.filter((kkm: any) => kkm.selected && kkm.quantity !== quantityAll).length,
        0,
    );

    // Оплата всегда вперёд: при заведённой будущей ставке считаем по ней (бэк в снапшоте заказа – так же)
    const effectivePrice = (kkm: any): number =>
        kkm.create_payment_info?.upcoming_payment_amount || kkm.create_payment_info?.payment_amount_by_rent || 0;

    const handleProceedToConfirm = () => {
        const includedMerchants: any[] = [];

        cartData.forEach(entity => {
            const includedKkm = entity.kkm_list.filter((kkm: any) => kkm.selected && !kkm.disabled);

            if (includedKkm.length > 0) {
                includedMerchants.push({
                    ...entity,
                    kkm_list: includedKkm,
                    payment_cash_count: includedKkm.length,
                    payment_cash_sum: includedKkm.reduce((sum: number, kkm: any) => sum + kkm.quantity * effectivePrice(kkm), 0),
                });
            }
        });

        setConfirmPaymentData({
            merchantListWithIncludedCash: includedMerchants,
            totalCount: totals.totalCount,
            totalSum: totals.totalSum,
        });

        setShowConfirmPayment(true);
    };

    const prepareData = (bundle: any) => {
        const combined: any[] = [];

        bundle.merchant_list.forEach((entity: any) => {
            if (entity.kkm_list?.length) {
                const kkm_list = entity.kkm_list.map((kkm: any) => ({
                    ...kkm,
                    selected: false, // !kkm.service_disconnect?.length && (kkm.balance_value < 0 || kkm.payment_amount_by_rent),
                    quantity: 1,
                    quantity_input: '1',
                    disabled:
                        kkm.service_disconnect?.length ||
                        kkm.create_payment_info?.balance_control_disabled ||
                        (kkm.create_payment_info?.payment_amount_by_rent === 0 && !kkm.create_payment_info?.upcoming_payment_amount),
                }));
                const entitySelected = kkm_list.every((kkm: any) => kkm.selected);
                const entitySum = kkm_list.reduce(
                    (sum: number, kkm: any) => (kkm.selected ? sum + (kkm.payment_amount_by_rent - kkm.balance_value) : sum),
                    0,
                );
                const entityCount = kkm_list.filter((kkm: any) => kkm.selected).length;
                combined.push({
                    ...entity,
                    kkm_list,
                    selected: entitySelected,
                    total: entitySum,
                    count: entityCount,
                });
            }
        });

        return combined;
    };

    const recalculateTotals = (data: any[]) => {
        let totalSum = 0;
        let totalCount = 0;
        data.forEach(entity => {
            entity.total = entity.kkm_list.reduce(
                (sum: number, kkm: any) => (kkm.selected ? sum + kkm.quantity * effectivePrice(kkm) : sum),
                0,
            );
            entity.count = entity.kkm_list.filter((kkm: any) => kkm.selected).length;
            totalSum += entity.total;
            totalCount += entity.count;
        });
        setTotals({totalSum, totalCount});
    };

    const handleEntityCheckbox = (entityId: number) => {
        const newData = cartData.map(entity => {
            if (entity.id !== entityId) return entity;
            const newVal = !entity.kkm_list.every((kkm: any) => kkm.disabled || kkm.selected);
            const updatedKkmList = entity.kkm_list.map((kkm: any) => (kkm.disabled ? kkm : {...kkm, selected: newVal}));
            return {
                ...entity,
                kkm_list: updatedKkmList,
                selected: newVal,
            };
        });
        setCartData(newData);
        recalculateTotals(newData);
    };

    const handleKkmCheckbox = (entityId: number, kkmId: number) => {
        const newData = cartData.map(entity => {
            if (entity.id !== entityId) return entity;

            const updatedKkmList = entity.kkm_list.map((kkm: any) => {
                if (kkm.kkm_id === kkmId && !kkm.disabled) {
                    return {...kkm, selected: !kkm.selected};
                }
                return kkm;
            });

            const allSelected = updatedKkmList.every((kkm: any) => kkm.disabled || kkm.selected);

            return {
                ...entity,
                kkm_list: updatedKkmList,
                selected: allSelected,
            };
        });

        setCartData(newData);
        recalculateTotals(newData);
    };

    const filteredData = cartData
        .filter(entity => {
            const companyMatch = entity.name.toLowerCase().includes(searchCompany.toLowerCase()) || entity.inn.includes(searchCompany);
            return companyMatch;
        })
        .map(entity => ({
            ...entity,
            kkm_list: entity.kkm_list.filter((kkm: any) => {
                const kkmMatch =
                    (kkm.reg_number && kkm.reg_number.includes(searchKkm)) ||
                    (kkm.serial_number && kkm.serial_number.includes(searchKkm)) ||
                    (kkm.department?.name && kkm.department.name.toLowerCase().includes(searchKkm.toLowerCase())) ||
                    (kkm.department?.address && kkm.department.address.toLowerCase().includes(searchKkm.toLowerCase()));

                if (searchKkm && !kkmMatch) return false;
                if (
                    hidePaid &&
                    (kkm.service_disconnect?.length ||
                        kkm.create_payment_info?.balance_control_disabled ||
                        (kkm.create_payment_info?.payment_amount_by_rent === 0 && !kkm.create_payment_info?.upcoming_payment_amount))
                )
                    return false;

                return true;
            }),
        }))
        .filter(entity => entity.kkm_list.length > 0);

    const getCartBundleRequest = async () => {
        const request: ICreateCartBundleRequest = {
            method: 'GetKKMList',
            params: {
                user_id: Number(userId),
            },
        };
        try {
            const response = await instance.post('/LK', request);
            handleErrorNotification(response);

            const prepared = prepareData(response.data);
            setCartData(prepared);
            recalculateTotals(prepared);
            setIsLoading(false);
        } catch (error) {
            console.error('Error CreateCartBundle:', error);

            if (error?.error === -3) {
                navigate('/');
            } else if (error?.error_description) {
                setErrorText(error?.error_description);
            } else {
                setErrorText('Ошибка формирования корзины.');
            }

            setIsLoading(false);
        }
    };

    const hasFetched = useRef(false);
    useEffect(() => {
        if (userId && !hasFetched.current) {
            hasFetched.current = true;
            void getCartBundleRequest();
        }
    }, [userId]);

    const selectAll = () => {
        const newData = cartData.map(entity => ({
            ...entity,
            selected: true,
            kkm_list: entity.kkm_list.map((kkm: any) => (kkm.disabled ? kkm : {...kkm, selected: true})),
        }));
        setCartData(newData);
        recalculateTotals(newData);
    };

    const deselectAll = () => {
        const newData = cartData.map(entity => ({
            ...entity,
            selected: false,
            kkm_list: entity.kkm_list.map((kkm: any) => (kkm.disabled ? kkm : {...kkm, selected: false})),
        }));
        setCartData(newData);
        recalculateTotals(newData);
    };

    const visibleKkmIds = new Set(filteredData.flatMap(entity => entity.kkm_list.map((kkm: any) => kkm.kkm_id)));

    const hiddenSelectedCount = cartData.reduce((count, entity) => {
        return count + entity.kkm_list.filter((kkm: any) => kkm.selected && !visibleKkmIds.has(kkm.kkm_id)).length;
    }, 0);

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${day}.${month}.${year}`;
    };

    const getTextColorByPaymentInfo = (statusColor: string | null) => {
        let textColor = 'textSecondary';

        if (statusColor === 'green') {
            textColor = 'success.main';
        } else if (statusColor === 'yellow') {
            textColor = 'warning.main';
        } else if (statusColor === 'red') {
            textColor = 'error.main';
        }

        return textColor;
    };

    return (
        <>
            {!showConfirmPayment ? (
                <Container
                    maxWidth="xl"
                    sx={{
                        pt: isSm ? 2 : 6,
                        pb: isSm ? 10 : 20,
                        minHeight: 600,
                        px: isSm ? 1 : 3,
                    }}
                >
                    {isLoading ? (
                        <CircularProgress
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                            }}
                        />
                    ) : errorText ? (
                        <>{errorText}</>
                    ) : (
                        <>
                            <Typography variant={isSm ? 'h5' : 'h4'} gutterBottom sx={{mb: isSm ? 2 : 3}}>
                                Оплата обслуживания касс
                            </Typography>
                            <Typography variant="body2" sx={{mb: isSm ? 2 : 3}}>
                                Оплата обслуживания касс по подключённым тарифам: «Прокси» (связь с любым ОФД) и, если подключён,
                                «Номенклатура».
                            </Typography>

                            <Box mb={isSm ? 2 : 4}>
                                <Paper>
                                    <Box p={isSm ? 2 : 3}>
                                        <Box display="flex" gap={2} flexWrap={isSm ? 'nowrap' : 'wrap'} flexDirection={isSm ? 'column' : 'row'}>
                                            <Box flex={isSm ? '1 1 auto' : '1 1 30%'}>
                                                <TextField
                                                    label="Поиск по компании"
                                                    value={searchCompany}
                                                    onChange={e => setSearchCompany(e.target.value)}
                                                    size="medium"
                                                    fullWidth
                                                />
                                            </Box>
                                            <Box flex={isSm ? '1 1 auto' : '1 1 30%'}>
                                                <TextField
                                                    label="Поиск по кассе"
                                                    value={searchKkm}
                                                    onChange={e => setSearchKkm(e.target.value)}
                                                    size="medium"
                                                    fullWidth
                                                />
                                            </Box>
                                            <Box
                                                flex={isSm ? '1 1 auto' : '1 1 30%'}
                                                display="flex"
                                                flexDirection="column"
                                                justifyContent="center"
                                                mt={isSm ? 2 : 0}
                                            >
                                                <FormControlLabel
                                                    control={<Checkbox checked={hidePaid} onChange={e => setHidePaid(e.target.checked)} />}
                                                    label={
                                                        <>
                                                            Показать только доступные для оплаты кассы
                                                            <HelpPopover
                                                                content={
                                                                    <>
                                                                        В этом случае будут скрыты кассы (услуги):
                                                                        <br />
                                                                        <br />
                                                                        — приостановленные;
                                                                        <br />
                                                                        — с отключенным контролем баланса;
                                                                        <br />
                                                                        — без начислений по тарифам (например, служебные).
                                                                        <br />
                                                                        <br />
                                                                        Просьба обратить внимание на причины, по которым услуга не может быть
                                                                        оплачена,&nbsp;— для избежания блокировки кассы.
                                                                    </>
                                                                }
                                                            />
                                                        </>
                                                    }
                                                    sx={{height: 25}}
                                                />
                                            </Box>
                                        </Box>
                                        <Divider sx={{mt: 3, mb: 3}} />
                                        <Box>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={isSm ? 1 : 2}
                                                mb={isSm ? 2 : 3}
                                                flexWrap={isSm ? 'wrap' : 'nowrap'}
                                            >
                                                <Typography variant="body1">Оплатить за</Typography>
                                                <MonthsStepper value={quantityAll} inputValue={quantityAllInput} onChange={handleQuantityAll} />
                                                <Typography variant="body1">
                                                    {pluralize(quantityAll, 'месяц', 'месяца', 'месяцев', false)} – всем кассам
                                                </Typography>
                                                {customMonthsCount > 0 && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        у {pluralize(customMonthsCount, 'кассы', 'касс', 'касс')} месяцы заданы отдельно
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box
                                                display="flex"
                                                alignItems={isSm ? 'flex-start' : 'top'}
                                                gap={isSm ? 2 : 5}
                                                flexWrap="wrap"
                                                flexDirection={isSm ? 'column' : 'row'}
                                            >
                                                <Box>
                                                    <Typography variant="h6">
                                                        Итого к оплате: <strong>{totals.totalSum} ₸</strong>
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {/*За {billingPeriod || '-'}*/}
                                                        По {pluralize(totals.totalCount, 'кассе', 'кассам', 'кассам')}
                                                    </Typography>
                                                </Box>
                                                {hiddenSelectedCount > 0 && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 1,
                                                            lineHeight: 1.4,
                                                            marginTop: '5px',
                                                            maxWidth: isSm ? '100%' : '400px',
                                                        }}
                                                    >
                                                        <Box component="span" sx={{mt: isSm ? '2px' : '0'}}>
                                                            ⚠️
                                                        </Box>
                                                        <span>
                                                            Оплата будет произведена в том числе по{' '}
                                                            {pluralize(
                                                                hiddenSelectedCount,
                                                                'скрытой кассе',
                                                                'скрытым кассам',
                                                                'скрытым кассам',
                                                            )}
                                                            .<br />
                                                            Очистите поиск и фильтры, чтобы увидеть все кассы.
                                                        </span>
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box mt={3}>
                                                <Button disabled={totals.totalSum === 0} onClick={handleProceedToConfirm}>
                                                    Оплатить выбранное
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>

                            <Box mb={isSm ? 2 : 4}>
                                <Paper>
                                    <Box pt={isSm ? 2 : 3} px={isSm ? 1 : 3} pb={1}>
                                        <Box display="flex" justifyContent="flex-start" gap={2} mb={isSm ? 2 : 4} flexWrap="wrap">
                                            <Typography
                                                component="span"
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                }}
                                                onClick={selectAll}
                                            >
                                                Выбрать все
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                }}
                                                onClick={deselectAll}
                                            >
                                                Снять выделение
                                            </Typography>
                                        </Box>

                                        {filteredData.map((entity, eIdx) => (
                                            <Box key={eIdx} mb={3}>
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <Checkbox
                                                        checked={
                                                            entity.kkm_list.filter((k: any) => !k.disabled).length > 0 &&
                                                            entity.kkm_list.filter((k: any) => !k.disabled).every((k: any) => k.selected)
                                                        }
                                                        disabled={entity.kkm_list.filter((k: any) => !k.disabled).length === 0}
                                                        onChange={() => handleEntityCheckbox(entity.id)}
                                                    />
                                                    <Box>
                                                        <Typography
                                                            fontWeight="bold"
                                                            sx={{
                                                                fontSize: isSm ? 14 : 16,
                                                                pr: 1,
                                                            }}
                                                        >
                                                            {entity.name} (ИИН/БИН: {entity.inn})
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{fontSize: isSm ? 12 : 14}}>
                                                            Кол-во касс: {entity.count} | Сумма: {entity.total} ₸
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        overflowX: 'auto',
                                                        WebkitOverflowScrolling: 'touch',
                                                        display: 'block',
                                                    }}
                                                >
                                                    <Table
                                                        size="small"
                                                        sx={{
                                                            marginBottom: 6,
                                                            minWidth: isSm ? 750 : 0,
                                                        }}
                                                    >
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>&nbsp;</TableCell>
                                                                <TableCell>Модель</TableCell>
                                                                <TableCell>Рег.&nbsp;номер</TableCell>
                                                                <TableCell>Сер.&nbsp;номер</TableCell>
                                                                <TableCell>ОФД</TableCell>
                                                                <TableCell>Место&nbsp;торговли</TableCell>
                                                                <TableCell>Тарифы</TableCell>
                                                                <TableCell>Баланс</TableCell>
                                                                <TableCell style={{minWidth: '120px'}}>
                                                                    Оплачено&nbsp;до
                                                                    <HelpPopover
                                                                        content={
                                                                            <>
                                                                                Дата, по которую включительно оплачена услуга.
                                                                                <br />
                                                                                <br />
                                                                                Прочерк – у касс без платного тарифа.
                                                                            </>
                                                                        }
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{width: 110, minWidth: 110}}>Мес.</TableCell>
                                                                <TableCell>
                                                                    <ShoppingCartOutlinedIcon fontSize={isSm ? 'small' : 'medium'} />
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {entity.kkm_list.map((kkm: any, kIdx: number) => (
                                                                <TableRow
                                                                    key={kIdx}
                                                                    sx={{
                                                                        color: kkm.disabled ? 'text.disabled' : 'inherit',
                                                                        '&:last-child td, &:last-child th': {
                                                                            borderBottom: 0,
                                                                        },
                                                                        backgroundColor: kkm.selected ? 'rgba(0,0,0,0.03)' : 'inherit',
                                                                        transition: 'background-color 0.2s ease',
                                                                        '&:hover': {
                                                                            backgroundColor: kkm.selected
                                                                                ? 'rgba(0,0,0,0.05)'
                                                                                : 'rgba(0,0,0,0.02)',
                                                                            cursor: 'pointer',
                                                                        },
                                                                    }}
                                                                    onClick={() => handleKkmCheckbox(entity.id, kkm.kkm_id)}
                                                                >
                                                                    <TableCell>
                                                                        <Checkbox
                                                                            size={isSm ? 'small' : 'medium'}
                                                                            checked={kkm.selected}
                                                                            disabled={!!kkm.disabled}
                                                                            onChange={() => handleKkmCheckbox(entity.id, kkm.kkm_id)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: kkm.disabled ? 'text.disabled' : 'inherit',
                                                                            maxWidth: 160,
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        }}
                                                                    >
                                                                        {kkm.model}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: kkm.disabled ? 'text.disabled' : 'inherit',
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {kkm.reg_number}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: kkm.disabled ? 'text.disabled' : 'inherit',
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {kkm.serial_number}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: kkm.disabled ? 'text.disabled' : 'inherit',
                                                                            maxWidth: 140,
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        }}
                                                                    >
                                                                        {kkm?.ofd?.name}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: kkm.disabled ? 'text.disabled' : 'inherit',
                                                                            maxWidth: 220,
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        }}
                                                                    >
                                                                        {kkm.department?.address
                                                                            ? kkm.department?.name + ' / ' + kkm.department?.address
                                                                            : '—'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <ServiceRentIcon kkm={kkm} />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                color={getTextColorByPaymentInfo(
                                                                                    kkm.create_payment_info?.color_status,
                                                                                )}
                                                                            >
                                                                                {kkm.create_payment_info?.balance_control_disabled ||
                                                                                (kkm.create_payment_info?.payment_amount_by_rent === 0 &&
                                                                                    !kkm.create_payment_info?.upcoming_payment_amount)
                                                                                    ? 0
                                                                                    : kkm.balance_value}
                                                                            </Typography>

                                                                            {kkm.create_payment_info?.is_new_kkm_created_before_cutoff &&
                                                                                !kkm.create_payment_info?.balance_control_disabled &&
                                                                                kkm.create_payment_info?.payment_amount_by_rent > 0 && (
                                                                                    <HelpPopover
                                                                                        customIcon={
                                                                                            <CardGiftcardOutlinedIcon
                                                                                                fontSize="small"
                                                                                                color="disabled"
                                                                                            />
                                                                                        }
                                                                                        content={
                                                                                            <>
                                                                                                Кассы, добавленные в первые дни месяца, работают
                                                                                                бесплатно до 5 числа этого месяца
                                                                                                (включительно).
                                                                                                <br />
                                                                                                <br />
                                                                                                Оплатите до этого срока, чтобы кассы продолжили
                                                                                                работать без перебоев.
                                                                                            </>
                                                                                        }
                                                                                    />
                                                                                )}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography
                                                                            variant="body2"
                                                                            color={getTextColorByPaymentInfo(
                                                                                kkm.create_payment_info?.color_status,
                                                                            )}
                                                                        >
                                                                            {kkm.create_payment_info?.paid_until_date}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        onClick={kkm.selected ? e => e.stopPropagation() : undefined}
                                                                        sx={{width: 110, minWidth: 110, whiteSpace: 'nowrap'}}
                                                                    >
                                                                        {kkm.selected && (
                                                                            <MonthsStepper
                                                                                compact
                                                                                value={kkm.quantity}
                                                                                inputValue={kkm.quantity_input ?? String(kkm.quantity)}
                                                                                onChange={(value, inputValue) =>
                                                                                    handleKkmQuantity(entity.id, kkm.kkm_id, value, inputValue)
                                                                                }
                                                                            />
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: kkm.disabled ? 'text.disabled' : 'inherit',
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {kkm.selected ? kkm.quantity * effectivePrice(kkm) : 0} ₸
                                                                        <span
                                                                            style={{
                                                                                display: 'inline-block',
                                                                                marginLeft: '10px',
                                                                                verticalAlign: 'middle',
                                                                            }}
                                                                        >
                                                                            <StatusIcon kkm={kkm} disabledByClientOnly={true} />
                                                                        </span>
                                                                        {!kkm.disabled && kkm.create_payment_info?.upcoming_payment_amount && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                display="block"
                                                                                color="text.secondary"
                                                                                sx={{lineHeight: 1.3}}
                                                                            >
                                                                                {kkm.create_payment_info.upcoming_payment_amount} ₸/мес
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    display="block"
                                                                                    color="text.disabled"
                                                                                    sx={{lineHeight: 1.3}}
                                                                                >
                                                                                    с {kkm.create_payment_info.upcoming_from_date}
                                                                                </Typography>
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Box>

                            <Paper>
                                <Box p={isSm ? 2 : 3}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={isSm ? 1 : 2}
                                        mb={isSm ? 2 : 3}
                                        flexWrap={isSm ? 'wrap' : 'nowrap'}
                                    >
                                        <Typography variant="body1">Оплатить за</Typography>
                                        <MonthsStepper value={quantityAll} inputValue={quantityAllInput} onChange={handleQuantityAll} />
                                        <Typography variant="body1">
                                            {pluralize(quantityAll, 'месяц', 'месяца', 'месяцев', false)} – всем кассам
                                        </Typography>
                                        {customMonthsCount > 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                у {pluralize(customMonthsCount, 'кассы', 'касс', 'касс')} месяцы заданы отдельно
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box
                                        display="flex"
                                        alignItems={isSm ? 'flex-start' : 'top'}
                                        gap={isSm ? 2 : 5}
                                        flexWrap="wrap"
                                        flexDirection={isSm ? 'column' : 'row'}
                                    >
                                        <Box>
                                            <Typography variant="h6">
                                                Итого к оплате: <strong>{totals.totalSum} ₸</strong>
                                            </Typography>
                                            <Typography variant="body1">
                                                {/*За {billingPeriod || '-'}*/}
                                                По {pluralize(totals.totalCount, 'кассе', 'кассам', 'кассам')}
                                            </Typography>
                                        </Box>
                                        {hiddenSelectedCount > 0 && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 1,
                                                    lineHeight: 1.4,
                                                    marginTop: '5px',
                                                    maxWidth: isSm ? '100%' : '400px',
                                                }}
                                            >
                                                <Box component="span" sx={{mt: isSm ? '2px' : '0'}}>
                                                    ⚠️
                                                </Box>
                                                <span>
                                                    Оплата будет произведена в том числе по{' '}
                                                    {pluralize(hiddenSelectedCount, 'скрытой кассе', 'скрытым кассам', 'скрытым кассам')}.
                                                    <br />
                                                    Очистите поиск и фильтры, чтобы увидеть все кассы.
                                                </span>
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box mt={3}>
                                        <Button disabled={totals.totalSum === 0} onClick={handleProceedToConfirm}>
                                            Оплатить выбранное
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>
                        </>
                    )}
                </Container>
            ) : (
                <Container maxWidth="lg" sx={{pt: 6, pb: 20, minHeight: 600}}>
                    <ConfirmPayment
                        userId={userId}
                        merchantListWithIncludedCash={confirmPaymentData.merchantListWithIncludedCash}
                        totalCount={confirmPaymentData.totalCount}
                        totalSum={confirmPaymentData.totalSum}
                        close={() => void setShowConfirmPayment(false)}
                    />
                </Container>
            )}
        </>
    );
};

export default CustomPaymentPage;
